import React, {useMemo, useState} from 'react'
import moment from 'moment'
import {Link} from 'react-router-dom'
import {Badge, Spinner, Table} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {ContactDocumentGroup} from './contactProfileTypes'
import {parseId} from '../../../../helpers'
import {prependBackendUrl} from '../../../../gql/client'
import {RenderFilePreview} from '../../../onePolicy/components/documents/RenderFilePreview'

const sp = ' '
const COLUMN_COUNT = 7

/** Nombre visible del archivo, con la extensión real del adjunto. */
const displayFileName = (name: string, ext: string) => (ext ? `${name}.${ext}` : name)

const personName = (person: {firstName?: string | null; lastName?: string | null}) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ')

interface ContactDocumentsTabProps {
  groups: ContactDocumentGroup[]
  loading: boolean
  onRefresh: () => void
  onNavigate?: () => void
}

/**
 * Documentos del cliente: los `Policy_attachments` de todas las pólizas donde el
 * contacto figura como miembro, agrupados por póliza. Solo lectura — la gestión
 * (subir/editar/eliminar) vive en el tab Documents de cada póliza.
 */
export const ContactDocumentsTab: React.FC<ContactDocumentsTabProps> = ({
  groups,
  loading,
  onRefresh,
  onNavigate,
}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const [filterName, setFilterName] = useState('')

  const filteredGroups = useMemo(() => {
    const needle = filterName.trim().toLowerCase()
    if (!needle) return groups
    return groups
      .map((group) => ({
        ...group,
        documents: group.documents.filter(
          (doc) =>
            doc.name?.toLowerCase().includes(needle) ||
            doc.documentTag?.tagName?.toLowerCase().includes(needle) ||
            `${doc.memberPolicy?.firstName ?? ''} ${doc.memberPolicy?.lastName ?? ''}`
              .toLowerCase()
              .includes(needle)
        ),
      }))
      .filter((group) => group.documents.length > 0)
  }, [groups, filterName])

  const total = filteredGroups.reduce((acc, group) => acc + group.documents.length, 0)

  return (
    <div className='policy-form-tabs-container'>
      <div className='kt-heading kt-heading--md custom-border-brand d-flex align-items-center justify-content-between'>
        <span>
          <FormattedMessage id='contacts.profile.documents.title' defaultMessage='Documents' />
        </span>
        <button
          type='button'
          className='btn btn-sm'
          style={{backgroundColor: '#d0e5fe', color: '#3e97ff'}}
          onClick={onRefresh}
          disabled={loading}
        >
          <i className='fa fa-redo' style={{color: 'inherit'}} />
          {sp}
          <FormattedMessage id='common.action.refresh' defaultMessage='Refresh' />
        </button>
      </div>

      <div className='row mb-3'>
        <div className='col-md-5'>
          <input
            type='text'
            className='form-control'
            placeholder={intl.formatMessage({
              id: 'contacts.profile.documents.searchPlaceholder',
              defaultMessage: 'Search by name, tag or member...',
            })}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : total === 0 ? (
        <div className='text-muted py-4 text-center'>
          <FormattedMessage
            id='contacts.profile.documents.empty'
            defaultMessage='There are no documents uploaded for this contact.'
          />
        </div>
      ) : (
        // Una sola tabla para todos los grupos: cabecera única y la póliza como fila
        // separadora, para que las columnas queden alineadas entre grupos.
        <Table responsive size='sm' className='mb-0 align-middle'>
          <thead>
            <tr>
              <th style={{width: 50}} />
              <th>
                <FormattedMessage id='contacts.profile.documents.col.file' defaultMessage='File' />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.documents.col.uploadedBy'
                  defaultMessage='Uploaded by'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.documents.col.member'
                  defaultMessage='Member'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.documents.col.expiration'
                  defaultMessage='Expiration'
                />
              </th>
              <th>
                <FormattedMessage id='contacts.profile.documents.col.tag' defaultMessage='Tag' />
              </th>
              <th style={{width: 60}} />
            </tr>
          </thead>
          {filteredGroups.map((group) => (
            <tbody key={group.policy.id}>
              <tr>
                <td colSpan={COLUMN_COUNT} className='bg-light'>
                  <div className='d-flex align-items-center gap-2 flex-wrap'>
                    {group.policy.typePolicy?.typePolicy && (
                      <Badge bg='primary' className='text-white'>
                        {group.policy.typePolicy.typePolicy}
                      </Badge>
                    )}
                    <Link
                      className='text-primary text-link fw-bold'
                      to={`/one-policy/${parseId(group.policy.id)}`}
                      onClick={onNavigate}
                    >
                      {group.policy.carrier?.name || group.policy.policyName || na}
                    </Link>
                    {group.policy.effectiveDate && (
                      <span className='text-muted' style={{fontSize: '0.9rem'}}>
                        <FormattedMessage
                          id='contacts.profile.documents.effective'
                          defaultMessage='Effective:'
                        />
                        {sp}
                        {moment(group.policy.effectiveDate).format('MMM DD, YYYY')}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
              {group.documents.map((doc) => {
                const ext = doc.attachments.split('?')[0].split('.')?.pop()?.toLowerCase() || ''
                return (
                  <tr key={doc.id}>
                    <td>
                      <RenderFilePreview file={prependBackendUrl(doc.attachments)} />
                    </td>
                    <td>{displayFileName(doc.name, ext)}</td>
                    <td>
                      <div>
                        {doc.user ? personName(doc.user) : na}
                        <div>
                          <small className='text-muted'>
                            {moment(doc.created).format('MMM DD, YYYY hh:mm A')}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{doc.memberPolicy ? personName(doc.memberPolicy) : ''}</td>
                    <td>
                      {doc.expirationDate ? moment(doc.expirationDate).format('MM/DD/YYYY') : ''}
                    </td>
                    <td>{doc.documentTag?.tagName || ''}</td>
                    <td>
                      <button
                        type='button'
                        className='btn-reset'
                        title={intl.formatMessage({
                          id: 'common.action.view',
                          defaultMessage: 'View',
                        })}
                        onClick={() => window.open(prependBackendUrl(doc.attachments), '_blank')}
                      >
                        <i className='bi bi-eye text-primary fs-2' />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </Table>
      )}
    </div>
  )
}
